import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styles from "./SearchAll.module.scss";
import InputForms from "../../UiComponents/forms/InputForms";
import { normalize } from "../../../utils/fonctionSearch";
import Axios from "../../../_services/caller.service";
import { decodedB64 } from "../../../CustomHooks/DecodedBase64";

const SERIAL_LABELS = {
  Bone_Serial: "CO",
  Bone_Soundcard_Serial: "Carte son CO",
  Headphones_Serial: "Casque",
  Headphones_Soundcard_Serial: "Carte son Casque",
  Inserts_Serial: "Inserts",
  Inserts_Soundcard_Serial: "Carte son Inserts",
  Operator_Serial: "Operator",
  Operator_Soundcard_Serial: "Carte son Operator",
  Speakers_MicrophoneSerial: "Micro Champ Libre",
  Headphones_MicrophoneSerial: "Micro Casque",
  Inserts_MicrophoneSerial: "Micro Inserts",
};

const MIN_CHARS = 2;
const MAX_RESULTS = 30;

// Checks if ALL words in the search match at least one field
const multiWordMatch = (fields, searchTerms) =>
  searchTerms.every((term) =>
    fields.some((f) => normalize(f).includes(term))
  );

// Highlight matching text
const highlightMatch = (text, searchTerms) => {
  if (!text) return "—";
  if (searchTerms.length === 0) return text;
  const normalized = normalize(text);
  const parts = [];
  let lastIndex = 0;

  // Find all match positions
  const matches = [];
  for (const term of searchTerms) {
    let idx = normalized.indexOf(term);
    while (idx !== -1) {
      matches.push({ start: idx, end: idx + term.length });
      idx = normalized.indexOf(term, idx + 1);
    }
  }

  if (matches.length === 0) return text;

  // Sort and merge overlapping
  matches.sort((a, b) => a.start - b.start);
  const merged = [matches[0]];
  for (let i = 1; i < matches.length; i++) {
    const prev = merged[merged.length - 1];
    if (matches[i].start <= prev.end) {
      prev.end = Math.max(prev.end, matches[i].end);
    } else {
      merged.push(matches[i]);
    }
  }

  for (const m of merged) {
    if (m.start > lastIndex) parts.push(text.slice(lastIndex, m.start));
    parts.push(<mark key={m.start} className={styles.highlight}>{text.slice(m.start, m.end)}</mark>);
    lastIndex = m.end;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const SearchAll = ({
  allUser,
  allCabines,
  changeOwnerIdWithSelect,
  changeCabIdWithSelect,
  ownerId,
  infosCabId,
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { t } = useTranslation();
  const [isSearching, setIsSearching] = useState(false);
  const [serialsMap, setSerialsMap] = useState({});
  const [serialsLoading, setSerialsLoading] = useState(true);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const resultsRef = useRef(null);
  const [visibleUsersLimit, setVisibleUsersLimit] = useState(MAX_RESULTS);
  const [visibleCabinesLimit, setVisibleCabinesLimit] = useState(MAX_RESULTS);

  // Debounce search input (250ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch serial numbers once on mount
  useEffect(() => {
    setSerialsLoading(true);
    Axios.get("/allCabinesSerials/")
      .then((res) => {
        if (res.data?.data) {
          const decoded = {};
          for (const [cabId, serials] of Object.entries(res.data.data)) {
            decoded[cabId] = {};
            for (const [fname, fval] of Object.entries(serials)) {
              try {
                decoded[cabId][fname] = decodedB64(fval);
              } catch {
                decoded[cabId][fname] = fval;
              }
            }
          }
          setSerialsMap(decoded);
        }
      })
      .catch(() => {})
      .finally(() => setSerialsLoading(false));
  }, []);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsSearching(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchTerms = useMemo(() =>
    normalize(debouncedSearch)
      .split(" ")
      .filter((t) => t.length > 0),
    [debouncedSearch]
  );

  const hasSearch = searchTerms.length > 0 && searchTerms.some((t) => t.length >= MIN_CHARS);
  const isTyping = search !== debouncedSearch;
  const dataLoading = allUser.length === 0 && allCabines.length === 0;

  // Filter users — multi-word match (memoized)
  const filteredUser = useMemo(() => {
    if (!hasSearch) return [];
    return allUser.filter((u) =>
      multiWordMatch(
        [u.display_name, u.user_email, u.ID.toString(), u.user_login],
        searchTerms
      )
    );
  }, [hasSearch, allUser, searchTerms]);

  // Filter cabines — match on name, id, AND serial numbers (memoized)
  const filteredCabines = useMemo(() => {
    if (!hasSearch) return [];
    return allCabines
      .map((c) => {
        const cabSerials = serialsMap[c.id] || {};
        const serialFields = Object.entries(cabSerials).map(
          ([fname, val]) => val
        );
        const allFields = [c.cabname, c.id.toString(), ...serialFields];

        const match = multiWordMatch(allFields, searchTerms);
        if (!match) return null;

        const matchedSerials = [];
        for (const [fname, val] of Object.entries(cabSerials)) {
          if (searchTerms.some((t) => normalize(val).includes(t))) {
            matchedSerials.push({ label: SERIAL_LABELS[fname], value: val });
          }
        }

        return { ...c, matchedSerials };
      })
      .filter(Boolean);
  }, [hasSearch, allCabines, serialsMap, searchTerms]);

  // Combined flat list for keyboard navigation
  const flatResults = useMemo(() => {
    const items = [];
    filteredUser.slice(0, MAX_RESULTS).forEach((u) => items.push({ type: "user", data: u }));
    filteredCabines.slice(0, MAX_RESULTS).forEach((c) => items.push({ type: "cabine", data: c }));
    return items;
  }, [filteredUser, filteredCabines]);

  const totalResults = filteredUser.length + filteredCabines.length;

  const handleSelectUser = useCallback((userId) => {
    changeOwnerIdWithSelect({ target: { value: userId } });
    setSearch("");
    setDebouncedSearch("");
    setIsSearching(false);
    setSelectedIndex(-1);
  }, [changeOwnerIdWithSelect]);

  const handleSelectCabine = useCallback((cabId) => {
    changeCabIdWithSelect({ target: { value: cabId } });
    setSearch("");
    setDebouncedSearch("");
    setIsSearching(false);
    setSelectedIndex(-1);
  }, [changeCabIdWithSelect]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isSearching || flatResults.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      const item = flatResults[selectedIndex];
      if (item.type === "user") handleSelectUser(item.data.ID);
      else handleSelectCabine(item.data.id);
    } else if (e.key === "Escape") {
      setIsSearching(false);
      setSelectedIndex(-1);
    }
  }, [isSearching, flatResults, selectedIndex, handleSelectUser, handleSelectCabine]);

  // Reset selection and limits when results change
  useEffect(() => {
    setSelectedIndex(-1);
    setVisibleUsersLimit(MAX_RESULTS);
    setVisibleCabinesLimit(MAX_RESULTS);
  }, [debouncedSearch]);

  // Determine what message to show
  const getStatusMessage = () => {
    const rawTerms = normalize(search).split(" ").filter((t) => t.length > 0);
    if (rawTerms.length > 0 && !rawTerms.some((t) => t.length >= MIN_CHARS)) {
      return t("superadmin_ui.search_min_chars", { count: MIN_CHARS });
    }
    if (isTyping) return t("superadmin_ui.searching", { defaultValue: "Recherche..." });
    if (dataLoading) return t("superadmin_ui.loading_data", { defaultValue: "Chargement des données..." });
    return null;
  };

  const showResults = isSearching && (hasSearch || (search.length > 0 && search.length < MIN_CHARS));

  return (
    <div className={styles.containerSearchAll} ref={containerRef}>
      <InputForms
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsSearching(true);
        }}
        onFocus={() => {
          if (search.length > 0) setIsSearching(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={t("superadmin_ui.search_all_placeholder")}
        id={styles.searchAll}
      />

      {showResults && (
        <div className={styles.results} ref={resultsRef}>
          {/* Status messages */}
          {getStatusMessage() && (
            <div className={styles.statusMessage}>{getStatusMessage()}</div>
          )}

          {/* Results */}
          {hasSearch && (
            <>
              {totalResults > 0 && (
                <div className={styles.totalCount}>
                  {t("superadmin_ui.total_results", {
                    count: totalResults,
                    defaultValue: `${totalResults} résultat(s)`,
                  })}
                </div>
              )}

              <div className={styles.group}>
                <p className={styles.groupTitle}>
                  {t("superadmin_ui.users_search_count", { count: filteredUser.length })}
                </p>
                <div className={styles.resultList}>
                  {filteredUser.length === 0 ? (
                    <div className={styles.noResult}>{t("superadmin_ui.no_result")}</div>
                  ) : (
                    filteredUser.slice(0, visibleUsersLimit).map((u, idx) => (
                      <div
                        key={u.ID}
                        className={`${styles.resultItem} ${selectedIndex === idx ? styles.resultItemActive : ""}`}
                        onClick={() => handleSelectUser(u.ID)}
                        onMouseEnter={() => setSelectedIndex(idx)}
                      >
                        <span className={styles.resultName}>
                          {highlightMatch(u.display_name || u.user_login, searchTerms)}
                        </span>
                        <span className={styles.resultSub}>
                          {highlightMatch(u.user_email, searchTerms)}
                        </span>
                      </div>
                    ))
                  )}
                  {filteredUser.length > visibleUsersLimit && (
                    <button
                      type="button"
                      className={styles.moreCount}
                      onClick={() => setVisibleUsersLimit((prev) => prev + MAX_RESULTS)}
                    >
                      +{filteredUser.length - visibleUsersLimit} {t("superadmin_ui.more_results", { defaultValue: "autres" })}
                    </button>
                  )}
                </div>
              </div>

              <div className={styles.group}>
                <p className={styles.groupTitle}>
                  {t("superadmin_ui.cabines_search_count", { count: filteredCabines.length })}
                </p>
                <div className={styles.resultList}>
                  {filteredCabines.length === 0 ? (
                    <div className={styles.noResult}>{t("superadmin_ui.no_result")}</div>
                  ) : (
                    filteredCabines.slice(0, visibleCabinesLimit).map((c, idx) => {
                      const globalIdx = Math.min(filteredUser.length, visibleUsersLimit) + idx;
                      return (
                        <div
                          key={c.id}
                          className={`${styles.resultItem} ${selectedIndex === globalIdx ? styles.resultItemActive : ""}`}
                          onClick={() => handleSelectCabine(c.id)}
                          onMouseEnter={() => setSelectedIndex(globalIdx)}
                        >
                          <span className={styles.resultName}>
                            {highlightMatch(c.cabname, searchTerms)}
                          </span>
                          <span className={styles.resultSub}>ID: {c.id}</span>
                          {c.matchedSerials?.length > 0 &&
                            c.matchedSerials.map((s, i) => (
                              <span key={i} className={styles.serialMatch}>
                                {s.label}: {highlightMatch(s.value, searchTerms)}
                              </span>
                            ))}
                        </div>
                      );
                    })
                  )}
                  {filteredCabines.length > visibleCabinesLimit && (
                    <button
                      type="button"
                      className={styles.moreCount}
                      onClick={() => setVisibleCabinesLimit((prev) => prev + MAX_RESULTS)}
                    >
                      +{filteredCabines.length - visibleCabinesLimit} {t("superadmin_ui.more_results", { defaultValue: "autres" })}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAll;